import { ref, reactive, onActivated, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete, Download } from '@element-plus/icons-vue';
import { request } from '@/util/globalContext';
import { listCommon } from '@/util/vueutil/commonList.js';

export default function useHolidayList() {
  const listRef = ref(null);
  const dialogVisible = ref(false);
  const dialogType = ref('add');
  const formRef = ref(null);
  const importLoading = ref(false);

  const searchForm = reactive({
    queryMonth: '',
    queryDate: ''
  });

  const {
    listShow,
    handleReset,
    handleSelectionChange,
    handleCurrentRowChange
  } = listCommon(searchForm);

  const FORM_DEFAULT = {
    id: '',
    holidayDate: '',
    holidayName: '',
    isHoliday: '1'
  };

  const formData = reactive({ ...FORM_DEFAULT });

  const formRules = {
    holidayDate: [
      { required: true, message: '请选择日期', trigger: 'change' }
    ],
    isHoliday: [
      { required: true, message: '请选择是否假期', trigger: 'change' }
    ]
  };

  const handleLoadList = async (paginationParams) => {
    if (!listRef.value) return;
    if (!listRef.value.isInited) {
      await nextTick();
      if (!listRef.value?.isInited) {
        ElMessage.warning('列表组件初始化中，请稍后重试');
        return;
      }
    }

    listRef.value?.setLoading(true);
    try {
      const params = {
        ...paginationParams,
        queryMonth: searchForm.queryMonth,
        queryDate: searchForm.queryDate
      };
      const res = await request.get('/appointmentHoliday/getPage', { params });
      if (listRef.value) {
        if (res.code === 200) {
          listRef.value.updateListData(res.data.records || []);
          listRef.value.updatePagination({
            pageNum: paginationParams.pageNum,
            pageSize: paginationParams.pageSize,
            total: res.data.total || 0
          });
        } else {
          ElMessage.error('列表加载失败：' + (res.msg || '接口异常'));
          listRef.value.updateListData([]);
          listRef.value.updatePagination({ ...paginationParams, total: 0 });
        }
      }
    } catch (error) {
      ElMessage.error('列表加载失败：网络异常');
      if (listRef.value) {
        listRef.value.updateListData([]);
        listRef.value.updatePagination({ ...paginationParams, total: 0 });
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleAdd = async () => {
    dialogType.value = 'add';
    formRef.value?.resetFields();
    Object.assign(formData, JSON.parse(JSON.stringify(FORM_DEFAULT)));
    dialogVisible.value = true;
  };

  const handleEdit = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择一条数据！');
      return;
    }

    try {
      listRef.value?.setLoading(true);
      const res = await request.get('/appointmentHoliday/getDetail', { params: { id: listShow.value.id } });
      if (res.code !== 200) {
        ElMessage.error(res.msg || '接口异常');
        return;
      }

      formData.id = res.data.id || '';
      formData.holidayName = res.data.holidayName || '';
      formData.isHoliday = res.data.isHoliday || '1';
      formData.holidayDate = '';

      if (res.data.holidayDate) {
        const date = new Date(res.data.holidayDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        formData.holidayDate = `${year}-${month}-${day}`;
      }

      dialogType.value = 'edit';
      dialogVisible.value = true;
    } catch (error) {
      ElMessage.error('获取详情失败：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择数据！');
      return;
    }

    try {
      await ElMessageBox.confirm('确定删除选中的假期？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      });

      listRef.value?.setLoading(true);
      const params = { id: listShow.value.id };
      const res = await request.post('/appointmentHoliday/delete', params);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success('删除成功');
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          ElMessage.error('删除失败：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      if (error !== 'cancel') {
        ElMessage.error('删除失败：网络异常');
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formRef.value) return;

    const isValid = await new Promise((resolve) => {
      formRef.value.validate((valid) => {
        resolve(valid);
      });
    });
    if (!isValid) return;

    const submitData = {
      id: dialogType.value === 'edit' ? formData.id : '',
      holidayDate: formData.holidayDate,
      holidayName: formData.holidayName,
      isHoliday: formData.isHoliday
    };

    listRef.value?.setLoading(true);
    try {
      const res = await request.post('/appointmentHoliday/saveOrUpdate', submitData);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success(dialogType.value === 'add' ? '新增成功' : '修改成功');
          dialogVisible.value = false;
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          const tip = dialogType.value === 'add' ? '新增失败' : '修改失败';
          ElMessage.error(tip + '：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      ElMessage.error('接口异常：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleImportYearHolidays = async () => {
    try {
      await ElMessageBox.confirm('此操作会重置本年日历，将清空已有假期数据并重新导入本年法定节假日。是否继续？', '重置确认', {
        confirmButtonText: '确定重置',
        cancelButtonText: '取消',
        type: 'warning'
      });

      importLoading.value = true;
      const res = await request.post('/appointmentHoliday/importYearHolidays', {});
      if (res.code === 200) {
        ElMessage.success(res.msg || '导入成功');
        const pagination = listRef.value?.getPagination();
        if (pagination) {
          handleLoadList(pagination);
        }
      } else {
        ElMessage.error('导入失败：' + (res.msg || '接口异常'));
      }
    } catch (error) {
      if (error !== 'cancel') {
        ElMessage.error('导入失败：网络异常');
      }
    } finally {
      importLoading.value = false;
    }
  };

  onActivated(async () => {
    if (listRef.value?.isInited) {
      const pagination = listRef.value.getPagination();
      handleLoadList(pagination);
    }
  });

  return {
    searchForm,
    listRef,
    dialogVisible,
    dialogType,
    formRef,
    listShow,
    formData,
    formRules,
    importLoading,
    Plus,
    Edit,
    Delete,
    Download,
    handleSelectionChange,
    handleCurrentRowChange,
    handleAdd,
    handleEdit,
    handleDelete,
    handleLoadList,
    handleReset,
    handleSubmit,
    handleImportYearHolidays
  };
}
